import { Card } from "@/components/ui/card";
import { Clock, FileUp, ArrowUpCircle, CheckCircle2, Circle, PencilLine } from "lucide-react";
import { useDispatchEvents } from "@/hooks/useDispatchEvents";
import { STATUS_LABEL, type DispatchStatus } from "@/hooks/useUpdateDispatchStatus";

interface Props {
  dispatchId: string | undefined;
  /** Optional externally-managed events (e.g. from useLoadDetail cache).
   *  When provided, this component skips its own fetch/subscription and
   *  renders directly from the prop — enabling optimistic updates. */
  events?: Array<{ id: string; event_type: string; payload: any; created_at: string; actor_name?: string }>;
}

function formatTs(iso: string) {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function eventLabel(e: { event_type: string; payload: any }): string {
  if (e.event_type.startsWith("status_")) {
    const s = e.event_type.replace("status_", "") as DispatchStatus;
    return `Status → ${STATUS_LABEL[s] ?? s}`;
  }
  if (e.event_type === "document_uploaded") return `Uploaded ${e.payload?.file_name ?? "document"}`;
  if (e.event_type === "dispatch_edited") {
    const n = Array.isArray(e.payload?.changed_fields) ? e.payload.changed_fields.length : 0;
    const suffix = n > 0 ? ` — ${n} ${n === 1 ? "field" : "fields"}` : "";
    return e.payload?.in_transit
      ? `Shipper updated dispatch while in transit${suffix}`
      : `Shipper updated dispatch${suffix}`;
  }
  return e.event_type.replace(/_/g, " ");
}

function eventIcon(eventType: string) {
  if (eventType.startsWith("status_delivered") || eventType.startsWith("status_closed"))
    return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
  if (eventType.startsWith("status_")) return <ArrowUpCircle className="h-3.5 w-3.5 text-brand-teal" />;
  if (eventType === "dispatch_edited") return <PencilLine className="h-3.5 w-3.5 text-amber-600" />;
  if (eventType.includes("document")) return <FileUp className="h-3.5 w-3.5 text-sky-600" />;
  return <Circle className="h-3.5 w-3.5 text-muted-foreground" />;
}

export function DispatchTimeline({ dispatchId, events: externalEvents }: Props) {
  const { events: hookEvents, loading } = useDispatchEvents(externalEvents ? undefined : dispatchId);
  const events = externalEvents ?? hookEvents;
  const isLoading = externalEvents ? false : loading;

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-4 w-4 text-brand-teal" />
        <h3 className="text-sm font-semibold uppercase tracking-wider">Timeline</h3>
      </div>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : events.length === 0 ? (
        <p className="text-xs text-muted-foreground">No activity yet.</p>
      ) : (
        <ol className="relative border-l border-border ml-1 space-y-3">
          {events.map((e) => (
            <li key={e.id} className="pl-4 relative">
              <span className="absolute -left-[7px] top-0.5 bg-card rounded-full">
                {eventIcon(e.event_type)}
              </span>
              <div className="text-sm font-medium text-foreground">{eventLabel(e)}</div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <span>{e.actor_name}</span>
                <span>·</span>
                <span>{formatTs(e.created_at)}</span>
              </div>
              {e.payload?.notes && (
                <div className="text-xs text-muted-foreground mt-0.5 italic">"{e.payload.notes}"</div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
